/**
 * nx-ownerapps-api の薄いクライアント
 *
 * - JWT を Authorization Bearer に自動付与
 * - 401 を受けたらトークンをクリアしてログイン画面へ誘導
 * - レスポンスは JSON 前提
 */
import { clearToken, getToken } from "./auth.ts";

const API_BASE: string = import.meta.env.VITE_OWNERAPPS_API_BASE || "http://127.0.0.1:8400";

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    clearToken();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }

  let payload: unknown = null;
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try { payload = await res.json(); } catch { /* empty body */ }
  }

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? "UNKNOWN",
      err?.message ?? `HTTP ${res.status}`,
      err?.details,
    );
  }
  return payload as T;
}

// === エンドポイントごとの薄いラッパ ===

export type RequestOtpResponse = { message: string; expires_in_seconds: number };

export async function requestOtp(email: string): Promise<RequestOtpResponse> {
  return request<RequestOtpResponse>("POST", "/auth/request-otp", { email });
}

export type VerifyOtpResponse = {
  access_token: string;
  token_type: "Bearer";
  expires_in_seconds: number;
  owner: { email: string; display_name: string };
};

export async function verifyOtp(email: string, otp: string): Promise<VerifyOtpResponse> {
  return request<VerifyOtpResponse>("POST", "/auth/verify-otp", { email, otp });
}

export type Me = {
  email: string;
  display_name: string;
  person_name: string;
  device_count: number;
};

export async function getMe(): Promise<Me> {
  return request<Me>("GET", "/me");
}

export type DeviceSummary = {
  mac: string;
  unit_serial: string;
  status: string;
  soc_bms_pct: number | null;
  voltage_v: number | null;
  last_seen_at: string | null;
  rough_address: string | null;
};

export async function getDevices(): Promise<{ devices: DeviceSummary[] }> {
  return request<{ devices: DeviceSummary[] }>("GET", "/me/devices");
}

export type DeviceDetail = {
  mac: string;
  status: string;
  soc_bms_pct: number | null;
  soc_ve_pct: number | null;
  voltage_v: number | null;
  cycle_count: number | null;
  last_seen_at: string | null;
  unit_serial?: string | null;
};

export async function getDevice(mac: string): Promise<DeviceDetail> {
  return request<DeviceDetail>("GET", `/me/devices/${encodeURIComponent(mac)}`);
}

export type HistoryRange = "30m" | "3h" | "1d" | "1w" | "1m" | "1y";

export type HistoryRow = {
  timestamp: string | null;
  soc_bms_pct: number | null;
  soc_ve_pct: number | null;
  voltage_v: number | null;
  input_w: number | null;
  output_w: number | null;
  net_w: number | null;
  cell_v_min: number | null;
  cell_v_max: number | null;
  cell_v_avg: number | null;
  temp_cell_1_c: number | null;
  temp_cell_2_c: number | null;
  temp_inverter_c: number | null;
};

export type HistoryResponse = {
  mac: string;
  range: HistoryRange;
  source: "raw" | "daily_summary";
  rows: HistoryRow[];
};

export async function getHistory(mac: string, range: HistoryRange): Promise<HistoryResponse> {
  return request<HistoryResponse>(
    "GET",
    `/me/devices/${encodeURIComponent(mac)}/history?range=${range}`,
  );
}

// === Push 購読 ===

export type PushSubscribeBody = {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  user_agent?: string;
};

export async function pushSubscribe(body: PushSubscribeBody): Promise<{ id: number; subscribed_at: string }> {
  return request("POST", "/me/push/subscribe", body);
}

export async function pushUnsubscribe(endpoint: string): Promise<void> {
  await request("POST", "/me/push/unsubscribe", { endpoint });
}

export type NativePlatform = "ios" | "android";

export type PushSubscribeNativeBody = {
  platform: NativePlatform;
  token: string;
  bundle_id?: string;
  user_agent?: string;
};

export async function pushSubscribeNative(
  body: PushSubscribeNativeBody,
): Promise<{ id: number; subscribed_at: string }> {
  return request("POST", "/me/push/subscribe-native", body);
}

export async function pushUnsubscribeNative(token: string): Promise<void> {
  await request("POST", "/me/push/unsubscribe-native", { token });
}

// === アラート ===

export type AlertItem = {
  id: number;
  timestamp: string | null;
  type: string;
  message: string;
  resolved: boolean;
};

export type AlertsResponse = {
  mac: string;
  total: number;
  limit: number;
  offset: number;
  alerts: AlertItem[];
};

export async function getAlerts(mac: string, limit = 10, offset = 0): Promise<AlertsResponse> {
  return request<AlertsResponse>(
    "GET",
    `/me/devices/${encodeURIComponent(mac)}/alerts?limit=${limit}&offset=${offset}`,
  );
}

// === 保証書 PDF ===

/** PDF を Blob で取得（JWT 必須なので直接 a.href で叩けない）。新規タブで開く。 */
export async function downloadWarrantyPdf(unitSerial: string): Promise<void> {
  const token = getToken();
  const res = await fetch(
    `${API_BASE}/me/warranty/${encodeURIComponent(unitSerial)}.pdf`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    throw new ApiError(res.status, "WARRANTY_DOWNLOAD_FAILED", `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `warranty_${unitSerial}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
